import { container } from 'tsyringe';
import type UIInterceptor from "@/UIInterceptors/UIInterceptor";
import type ViewModel from "@/ViewModels/ViewModel";
import type Control from "@/Controls/Control";
import type Factory from "@/Factories/Factory";
import type Message from "@/Messages/Message";
import type SummaryView from "@/SummaryViews/SummaryView";
import type UIListener from "@/UIListener/UIListener";
import type View from "@/Views/View";

/**
 * Mögliche Typen, die vom ComponentManager verwaltet werden können
 */
type ComponentType =
    'models' |
    'controls' |
    'factories' |
    'messages' |
    'summaryViews' |
    'uiListener' |
    'uiInterceptors' |
    'viewModels' |
    'views' |
    'utilities';

type Constructor<T = any, TArgs extends any[] = any[]> = new (...args: TArgs) => T;

/**
 * Hilfstyp, welcher entweder den genauen Konstruktor-Typen (samt korrekt typisierter Parameter!) oder einen
 * allgemeineren Konstruktor-Typen (mit any[] als Parameter) zurückgibt.
 */
type ExactOrGeneralConstructor<T> = T extends Constructor ? T : Constructor<T>;

/**
 * Der ComponentManager verwaltet Mappings von Aliases auf die jeweilige Komponente
 */
export default class ComponentManager {

    private extensionParameterAreaIdx: number;

    /**
     * Die Namen der zu priorisierenden Extensions, falls mehrere Komponenten unter demselben Namen registriert sind.
     * Ein niedrigerer Index bedeutet eine höhere Priorität.
     */
    private readonly prioritizedExtensions: string[];

    private registeredAliases: Map<ComponentType, Set<string>>;

    private static instance: ComponentManager;

    private constructor() {
        this.extensionParameterAreaIdx = 0;

        this.registeredAliases = new Map<ComponentType, Set<string>>();
        this.prioritizedExtensions = [];

        if (MODEL.Extension) {
            const extension = MODEL.Extension.split(',');

            for (let i = extension.length - 1; i >= 0; i--) {
                this.prioritizedExtensions.splice(0, 0, extension[i]);
            }

            this.extensionParameterAreaIdx = this.prioritizedExtensions.length;
        }
    }

    private static getInstance() {
        if (ComponentManager.instance == null)
            ComponentManager.instance = new ComponentManager();

        return ComponentManager.instance;
    }

    private setComponent(componentType: ComponentType, alias: string, ctor: Constructor, extension: string) {
        if (alias == null)
            throw 'Alias is required';

        if (ctor == null)
            throw 'Constructor is required';

        if (extension == null)
            throw 'Extension is required';

        this.trackAlias(componentType, alias);

        const token = this.createToken(componentType, alias, extension);
        container.register(token, { useValue: ctor });
    }

    private setConditionalComponent(componentType: ComponentType, alias: string, func: () => Constructor, extension: string) {
        if (alias == null)
            throw 'Alias is required';

        if (func == null)
            throw 'Func is required';

        if (extension == null)
            throw 'Extension is required';

        this.trackAlias(componentType, alias);

        const token = this.createToken(componentType, alias, extension);
        container.register(token, { useFactory: (_) => func() });
    }

    private trackAlias(componentType: ComponentType, alias: string) {
        let aliases = this.registeredAliases.get(componentType);
        if (aliases == null) {
            aliases = new Set<string>();
            this.registeredAliases.set(componentType, aliases);
        }
        aliases.add(alias);
    }

    private createToken(componentType: ComponentType, alias: string, extension: string) {
        return `${componentType}#${alias}#${extension}`.toLowerCase();
    }

    /**
     * Eine interne Funktion zum Auslesen einer Komponente zu einem Alias
     * Falls mehrere Komponenten unter einem Alias registriert sind, wird die zuletzt registrierte zurück geliefert.
     */
    private getComponent<T>(componentType: ComponentType, alias: string): T {
        for (const extension of this.prioritizedExtensions) {
            const token = this.createToken(componentType, alias, extension);
            if (!container.isRegistered(token))
                continue;

            return container.resolve<T>(token);
        }

        console.error(`ComponentManager: ${alias} could not be found.`)
        return null;
    }

    private getComponents<T>(componentType: ComponentType): T[] {
        const result: T[] = [];
        for (const alias of this.registeredAliases.get(componentType))
        for (const extension of this.prioritizedExtensions) {
            const token = this.createToken(componentType, alias, extension);
            if (container.isRegistered(token))
                result.push(container.resolve<T>(token));
        }
        return result;
    }

    /*
     * Ruft über einen Backend-Call alle verfügbaren Extensions bzw. Plugins ab.
     * Annahme: Das Backend liefert die Extensions bereits in der korrekt priorisierten Reihenfolge.
     */
    private async loadAllExtensions(): Promise<void> {
        const response = await fetch(`${MODEL.HTMLClientBaseURL}HTMLClient/Extension/All`, {
            method: 'GET',
            cache: 'no-store',
        });
        if (!response.ok)
            throw new Error('Failed to load components');

        const names: string[] = await response.json();
        for (const name of names) {
            if (!(name in this.prioritizedExtensions)) {
                this.prioritizedExtensions.splice(this.extensionParameterAreaIdx, 0, name);
            }
        }
    }

    /**
     * Registriert eine Komponente vom Typ "Model"
     */
    public static setModel<T>(alias: string, componentCtor: Constructor<T>, extension: string): void {
        ComponentManager.getInstance().setComponent('models', alias, componentCtor, extension);
    }

    /**
     * Registriert eine Komponente vom Typ "Control"
     */
    public static setControl<T extends Control>(alias: string, componentCtor: Constructor<T>, extension: string): void {
        ComponentManager.getInstance().setComponent('controls', alias, componentCtor, extension);
    }

    public static setConditionalControl<T extends Control>(alias: string, func: () => Constructor<T>, extension: string): void {
        ComponentManager.getInstance().setConditionalComponent('controls', alias, func, extension);
    }

    /**
     * Registriert eine Komponente vom Typ "Factory"
     */
    public static setFactory<T extends Factory>(alias: string, componentCtor: Constructor<T>, extension: string): void {
        ComponentManager.getInstance().setComponent('factories', alias, componentCtor, extension);
    }

    public static setConditionalFactory<T extends Factory>(alias: string, func: () => Constructor<T>, extension: string): void {
        ComponentManager.getInstance().setConditionalComponent('factories', alias, func, extension);
    }

    /**
     * Registriert eine Komponente vom Typ "Message"
     */
    public static setMessage<T extends Message>(alias: string, componentCtor: Constructor<T>, extension: string): void {
        ComponentManager.getInstance().setComponent('messages', alias, componentCtor, extension);
    }

    /**
     * Registriert eine Komponente vom Typ "SummaryView"
     */
    public static setSummaryView<T extends SummaryView>(alias: string, componentCtor: Constructor<T>, extension: string): void {
        ComponentManager.getInstance().setComponent('summaryViews', alias, componentCtor, extension);
    }

    /**
     * Registriert eine Komponente vom Typ "UIListener"
     */
    public static setUIListener<T extends UIListener>(alias: string, componentCtor: Constructor<T>, extension: string): void {
        ComponentManager.getInstance().setComponent('uiListener', alias, componentCtor, extension);
    }

    /**
     * Registriert eine Komponente vom Typ "UIInterceptor"
     */
    public static setUIInterceptor<T extends UIInterceptor>(alias: string, componentCtor: Constructor<T>, extension: string): void {
        ComponentManager.getInstance().setComponent('uiInterceptors', alias, componentCtor, extension);
    }

    /**
     * Registriert eine Komponente vom Typ "ViewModel"
     */
    public static setViewModel<T extends ViewModel>(alias: string, componentCtor: Constructor<T>, extension: string): void {
        ComponentManager.getInstance().setComponent('viewModels', alias, componentCtor, extension);
    }

    public static setConditionalViewModel<T extends ViewModel>(alias: string, func: () => Constructor<T>, extension: string): void {
        ComponentManager.getInstance().setConditionalComponent('viewModels', alias, func, extension);
    }

    /**
     * Registriert eine Komponente vom Typ "View"
     */
    public static setView<T extends View>(alias: string, componentCtor: Constructor<T>, extension: string): void {
        ComponentManager.getInstance().setComponent('views', alias, componentCtor, extension);
    }

    /**
     * Registriert eine Komponente vom Typ "Utility"
     */
    public static setUtility<T>(alias: string, componentCtor: Constructor<T>, extension: string): void {
        ComponentManager.getInstance().setComponent('utilities', alias, componentCtor, extension);
    }

    /**
     * Liefert alle registrierten "Factory"-Komponenten
     */
    public static getFactories(): string[] {
        return ComponentManager.getInstance().getAliases('factories');
    }

    /**
     * Liefert alle registrierten "ViewModel"-Komponenten
     */
    public static getViewModels(): string[] {
        return ComponentManager.getInstance().getAliases('viewModels');
    }

    /**
     * Liefert alle registrierten "Control"-Komponenten
     */
    public static getControls(): string[] {
        return ComponentManager.getInstance().getAliases('controls');
    }

    private getAliases(componentType: ComponentType): string[] {
        return [...this.registeredAliases.get(componentType)];
    }

    /**
     * Liefert alle "UIInterceptor"-Komponenten
     */
    public static getUIInterceptors(): (typeof UIInterceptor)[] {
        return ComponentManager.getInstance().getComponents<typeof UIInterceptor>('uiInterceptors');
    }

    /**
     * Liefert die zum Alias registrierte "Model"-Komponente
     */
    public static getModel<T>(alias: string): ExactOrGeneralConstructor<T> {
        return ComponentManager.getInstance().getComponent<ExactOrGeneralConstructor<T>>('models', alias);
    }

    /**
     * Liefert die zum Alias registrierte "Control"-Komponente
     */
    public static getControl<T>(alias: string): ExactOrGeneralConstructor<T> {
        return ComponentManager.getInstance().getComponent<ExactOrGeneralConstructor<T>>('controls', alias);
    }

    /**
     * Liefert die zum Alias registrierte "Factory"-Komponente
     */
    public static getFactory<T>(alias: string): ExactOrGeneralConstructor<T> {
        return ComponentManager.getInstance().getComponent<ExactOrGeneralConstructor<T>>('factories', alias);
    }

    /**
     * Liefert die zum Alias registrierte "Message"-Komponente
     */
    public static getMessage<T>(alias: string): ExactOrGeneralConstructor<T> {
        return ComponentManager.getInstance().getComponent<ExactOrGeneralConstructor<T>>('messages', alias);
    }

    /**
     * Liefert die zum Alias registrierte "SummaryView"-Komponente
     */
    public static getSummaryView<T>(alias: string): ExactOrGeneralConstructor<T> {
        return ComponentManager.getInstance().getComponent<ExactOrGeneralConstructor<T>>('summaryViews', alias);
    }

    /**
     * Liefert die zum Alias registrierte "UIListener"-Komponente
     */
    public static getUIListener<T>(alias: string): ExactOrGeneralConstructor<T> {
        return ComponentManager.getInstance().getComponent<ExactOrGeneralConstructor<T>>('uiListener', alias);
    }

    /**
     * Liefert die zum Alias registrierte "UIInterceptor"-Komponente
     */
    public static getUIInterceptor<T>(alias: string): ExactOrGeneralConstructor<T> {
        return ComponentManager.getInstance().getComponent<ExactOrGeneralConstructor<T>>('uiInterceptors', alias);
    }

    /**
     * Liefert die zum Alias registrierte "ViewModel"-Komponente
     */
    public static getViewModel<T>(alias: string): ExactOrGeneralConstructor<T> {
        return ComponentManager.getInstance().getComponent<ExactOrGeneralConstructor<T>>('viewModels', alias);
    }

    /**
     * Liefert die zum Alias registrierte "ViewModel"-Komponente
     */
    public static getViewModelByInterface<T>(alias: string): ExactOrGeneralConstructor<T> {
        return ComponentManager.getInstance().getComponent<ExactOrGeneralConstructor<T>>('viewModels', alias);
    }

    /**
     * Liefert die zum Alias registrierte "View"-Komponente
     */
    public static getView<T>(alias: string): T {
        return ComponentManager.getInstance().getComponent<T>('views', alias);
    }

    /**
     * Liefert die zum Alias registrierte "Utility"-Komponente
     */
    public static getUtility<T>(alias: string): ExactOrGeneralConstructor<T> {
        return ComponentManager.getInstance().getComponent<ExactOrGeneralConstructor<T>>('utilities', alias);
    }

    /**
     * Initialisiert den ComponentManager und macht ihn global über das Window-Objekt verfügbar
     * Komponenten aus Plugins werden geladen und registriert
     */
    public static async init(): Promise<void> {
        return ComponentManager.getInstance().loadAllExtensions();
    }
}